'use client';

import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState, useMemo } from 'react';
import { usePopper } from 'react-popper';
import { useOnClickOutside } from 'usehooks-ts';
import { ChevronIcon } from 'mode-shared/components/icons';

export type SelectOption<T> = {
  label: string;
  value: T;
};

export const SelectWithSearch = <T,>({
  searchable = true,
  options,
  onToggle,
  disabled,
  selectedOption,
  onSelect,
  searchPlaceholder = 'Search...',
  className,
}: {
  searchable?: boolean;
  options: SelectOption<T>[];
  onToggle?: (newState: boolean) => void;
  disabled?: boolean;
  selectedOption: SelectOption<T>;
  onSelect: (val: SelectOption<T>) => void;
  searchPlaceholder?: string;
  className?: string;
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [popperElement, setPopperElement] = useState<HTMLElement>();
  const [referenceElement, setReferenceElement] = useState<HTMLElement>();

  useOnClickOutside(
    [
      { current: popperElement } as React.RefObject<HTMLElement>,
      { current: referenceElement } as React.RefObject<HTMLElement>,
    ],
    () => {
      setOpen(false);
      setSearchQuery('');
    }
  );

  const { styles, attributes } = usePopper(referenceElement, popperElement, {
    placement: 'bottom-end',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 8,
        },
      },
    ],
  });

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return options;
    return options.filter((option) => option.label.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [options, searchQuery]);

  const handleOptionClick = (option: SelectOption<T>) => {
    onSelect(option);
    setOpen(false);
    setSearchQuery('');
  };

  useEffect(() => {
    if (onToggle) onToggle(open);
  }, [open, onToggle]);

  return (
    <div className={clsx(className, 'text-neutral-500 z-10')}>
      <button
        ref={setReferenceElement as React.LegacyRef<HTMLButtonElement>}
        disabled={disabled}
        onClick={() => setOpen(!open)}
        type="button"
        className={clsx(
          'flex items-center p-2 font-medium border bg-grey-800 rounded border-grey-900 w-[120px]',
          disabled && 'opacity-40 pointer-events-none'
        )}
        id="options-menu"
        aria-haspopup="listbox"
      >
        <div className="flex justify-between w-full">
          <div className="flex items-center">
            <span className="text-neutral-100 font-normal whitespace-nowrap overflow-hidden overflow-ellipsis text-xs">
              {selectedOption.label}
            </span>
          </div>
          <ChevronIcon size={24} className={clsx(open && 'rotate-180')} />
        </div>
      </button>

      <AnimatePresence>
        {open && options.length > 0 && (
          <div
            ref={setPopperElement as React.LegacyRef<HTMLDivElement>}
            style={{
              ...styles.popper,
              width: referenceElement?.offsetWidth,
            }}
            {...attributes.popper}
          >
            <motion.div
              initial={{ opacity: 0, transform: 'translateY(20px)' }}
              animate={{ opacity: 1, transform: 'translateY(0px)' }}
              exit={{ opacity: 0 }}
              className="w-full rounded-lg border border-grey-900 bg-grey-800 min-w-[120px]"
            >
              {searchable && (
                <div className="p-2 border-b border-grey-900">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={searchPlaceholder}
                    className="w-full bg-grey-800 text-neutral-100 text-xs border border-grey-900 rounded px-2 py-1 focus:outline-none focus:border-neutral-500"
                    autoFocus
                  />
                </div>
              )}
              <ul
                role="listbox"
                aria-labelledby="options-menu"
                aria-activedescendant="selected-option"
                className="max-h-[212px] overflow-y-auto py-1"
              >
                {filteredOptions.map((option, ix) => (
                  <li
                    key={`${option.label}-${ix}`}
                    onClick={() => handleOptionClick(option)}
                    className="cursor-pointer select-none relative px-2 py-2 flex items-center hover:bg-grey-700"
                  >
                    <div className="flex items-center space-x-2 w-full">
                      <span className="text-neutral-100 whitespace-nowrap overflow-hidden overflow-ellipsis text-xs">
                        {option.label}
                      </span>
                    </div>
                  </li>
                ))}
                {filteredOptions.length === 0 && (
                  <li className="px-2 py-2 text-neutral-400 text-xs text-center">No results found</li>
                )}
              </ul>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};
